import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Radio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import { ValidationErrors } from 'final-form';
import React from 'react';
import { Field, useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { AdCampaignBudgetFormValues, AdCampaignBudgetType } from 'modules/campaigns/types';

import { HelperText } from '../../../HelperText';
import { formatInputValueToDecimal } from '../../schedule-section/utils';
import { BudgetOption } from '../../types';
import { BudgetOptionSelector } from '../BudgetOptionSelector';

type WeeklyBudgetFormModalProps = {
  budgetAmount: number;
  budgetType: AdCampaignBudgetType;
  customBidAmount?: number;
  budgetOptions: BudgetOption[];
  isCampaignCompleted: boolean;
  errors: ValidationErrors;
  maxBidAmount: number;
  minBidAmount: number;
  minWeeklyBudget: number;
  maxWeeklyBudget: number;
  isDirty: boolean;
  hasCampaignStarted?: boolean;
  onCloseClick(): void;
  onSubmit(): void;
};

const StyledWrapper = styled.div`
  background-color: ${cssThemedValue('surfaceMain')};
  min-width: 800px;
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledContentWrapper = styled(ModalContent)`
  padding: 0;
`;

const StyledSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 752px;
  gap: 16px;
  > div {
    flex: 1;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  &:not(:last-of-type) {
    border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const StyledTitle = styled(Typography.Body2).attrs({ fontWeight: 'bold' })`
  display: block;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledInput = styled(Input)`
  width: 175px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const ColumnRadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  gap: 16px;
`;

const StyledTag = styled(Tag)`
  letter-spacing: 0.5px;
  line-height: 1em;
  margin-inline-start: 8px;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const BidStrategyDescription = styled(Typography.Small)`
  color: ${cssThemedValue('textSecondary')};
  padding-inline-start: 24px;
`;

const StyledErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: white;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

export const AdCampaignBudgetFormModal = ({
  onSubmit,
  budgetOptions,
  budgetAmount,
  customBidAmount,
  errors,
  maxBidAmount,
  minBidAmount,
  minWeeklyBudget,
  maxWeeklyBudget,
  isCampaignCompleted,
  isDirty,
  hasCampaignStarted = false,
  onCloseClick,
}: WeeklyBudgetFormModalProps) => {
  const { formatMessage, locale } = useIntl();
  const budgetTypeModalFormFieldValue = useField<AdCampaignBudgetType>('budgetType').input.value;
  const bidStrategyModalField = useField('bidStrategy');
  const { currency } = useCampaignModuleContext();
  return (
    <StyledWrapper data-testid="weekly-budget">
      <ModalHeader onCloseClick={onCloseClick}>
        <Typography.Heading6>
          <FormattedMessage id="campaigns.ads.budget-and-bidding" />
        </Typography.Heading6>
      </ModalHeader>
      <StyledContentWrapper>
        <StyledSection>
          <StyledTitle>
            <FormattedMessage id="campaigns.ads.budget.weekly-budget" />
          </StyledTitle>
          <StyledSectionContent>
            <BudgetOptionSelector
              name="budgetType"
              budgetOptions={budgetOptions}
              isDisabled={isCampaignCompleted || hasCampaignStarted}
              defaultValue={budgetTypeModalFormFieldValue}
            />
          </StyledSectionContent>
        </StyledSection>

        {budgetTypeModalFormFieldValue === 'custom' ? (
          <StyledSection>
            <StyledTitle>
              <FormattedMessage id="campaigns.ads.budget.custom-weekly-budget" />
            </StyledTitle>
            <StyledSectionContent>
              <Field<AdCampaignBudgetFormValues['budgetAmount']>
                name="budgetAmount"
                type="number"
                key="custom"
                defaultValue={budgetAmount}
              >
                {({ input }) => {
                  return (
                    <StyledInput
                      {...input}
                      value={input.value.toLocaleString(locale, {
                        useGrouping: true,
                      })}
                      onChange={e => {
                        const value = formatInputValueToDecimal(e.currentTarget.value);
                        input.onChange(value);
                      }}
                      type="text"
                      onBlur={e => {
                        // Make sure we remove any trailing decimal points and round up to 2 decimals when the user leaves the input
                        let numericValue = Number(e.target.value);
                        numericValue = Math.round(numericValue * 100) / 100;
                        input.onChange(numericValue.toFixed(2));
                      }}
                      suffixText={currency}
                      disabled={isCampaignCompleted || hasCampaignStarted}
                      data-testid="custom-weekly-budget-input"
                    />
                  );
                }}
              </Field>
            </StyledSectionContent>
            {!!errors?.budgetAmount && (
              <StyledErrorMessage>
                <FormattedMessage
                  id={errors.budgetAmount}
                  values={{
                    amount: (
                      <MonetaryAmount
                        amount={errors.budgetAmount.includes('maximum-budget') ? maxWeeklyBudget : minWeeklyBudget}
                        currency={currency}
                      />
                    ),
                  }}
                />
              </StyledErrorMessage>
            )}
            <HelperText text={formatMessage({ id: 'campaigns.ads.budget.budget-helper-text' })} />
          </StyledSection>
        ) : null}
        <StyledSection>
          <StyledTitle>
            <FormattedMessage id="campaigns.ads.bid.bid-strategy" />
          </StyledTitle>

          <ColumnRadioGroup>
            <Field<AdCampaignBudgetFormValues['bidStrategy']> name="bidStrategy" type="radio" value="auto">
              {({ input }) => (
                <RadioWrapper>
                  <Radio {...input}>
                    <Typography.Body3>
                      <FormattedMessage id="campaigns.ads.bid.automatic-bidding" />
                    </Typography.Body3>
                    <StyledTag variant="lightBlack">
                      <Typography.Overline4>
                        <FormattedMessage id="campaigns.recommended" />
                      </Typography.Overline4>
                    </StyledTag>
                  </Radio>
                  <BidStrategyDescription>
                    <FormattedMessage id="campaigns.ads.bid.automatic-bidding-description" />
                  </BidStrategyDescription>
                </RadioWrapper>
              )}
            </Field>
            <Field<AdCampaignBudgetFormValues['bidStrategy']> name="bidStrategy" type="radio" value="custom">
              {({ input }) => (
                <RadioWrapper>
                  <Radio {...input}>
                    <Typography.Body3>
                      <FormattedMessage id="campaigns.ads.bid.custom-bidding" />
                    </Typography.Body3>
                  </Radio>
                  <BidStrategyDescription>
                    <FormattedMessage id="campaigns.ads.bid.custom-bidding-description" />
                  </BidStrategyDescription>
                </RadioWrapper>
              )}
            </Field>
          </ColumnRadioGroup>
        </StyledSection>
        {bidStrategyModalField.input.value === 'custom' && (
          <StyledSection>
            <StyledTitle>
              <FormattedMessage id="campaigns.ads.bid.bid-amount" />
            </StyledTitle>
            <StyledSectionContent>
              <Typography.Body3>
                <FormattedMessage id="campaigns.ads.bid.bid-description" />
              </Typography.Body3>
              <Typography.Body3>
                <FormattedMessage id="campaigns.ads.bid.bid-auction-info" />
              </Typography.Body3>

              <Field<AdCampaignBudgetFormValues['customBidAmount']>
                name="customBidAmount"
                type="number"
                key="customBidAmount"
                defaultValue={customBidAmount}
              >
                {({ input }) => {
                  return (
                    <StyledInput
                      {...input}
                      value={
                        input.value
                          ? input.value.toLocaleString(locale, {
                              useGrouping: true,
                            })
                          : ''
                      }
                      onChange={e => {
                        const value = formatInputValueToDecimal(e.currentTarget.value);
                        input.onChange(value);
                      }}
                      type="text"
                      onBlur={e => {
                        // Make sure we remove any trailing decimal points and round up to 2 decimals when the user leaves the input
                        let numericValue = Number(e.target.value);
                        numericValue = Math.round(numericValue * 100) / 100;
                        input.onChange(numericValue.toFixed(2));
                      }}
                      suffixText={currency}
                      disabled={isCampaignCompleted}
                    />
                  );
                }}
              </Field>
            </StyledSectionContent>
            {!!errors?.customBidAmount && !errors.customBidAmount.includes('required') && (
              <StyledErrorMessage>
                <FormattedMessage
                  id={errors.customBidAmount}
                  values={{
                    amount: (
                      <MonetaryAmount
                        amount={
                          errors.customBidAmount.includes('minimum-bid-amount') && minBidAmount
                            ? minBidAmount
                            : maxBidAmount
                        }
                        currency={currency}
                      />
                    ),
                  }}
                />
              </StyledErrorMessage>
            )}
          </StyledSection>
        )}
      </StyledContentWrapper>
      <StyledFooter>
        <Button
          disabled={!(isDirty && (!errors || !Object.keys(errors).length))}
          variant="blue"
          size="small"
          onClick={onSubmit}
        >
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </StyledFooter>
    </StyledWrapper>
  );
};
